import { Outlet, createRootRoute, HeadContent, Scripts, Link } from "@tanstack/react-router";
import * as React from "react";
import { lazy, Suspense } from "react";
import { ThemeProvider } from "@/components/ThemeProvider";
import { JsonLd } from "@/components/JsonLd";
import { organizationSchema, websiteSchema, professionalServiceSchema } from "@/lib/schemas";
import { SITE_URL } from "@/lib/seo";
import appCss from "../styles.css?url";

const Analytics = lazy(() => import("@/components/Analytics").then((m) => ({ default: m.Analytics })));
const StickyCTA = lazy(() => import("@/components/StickyCTA").then((m) => ({ default: m.StickyCTA })));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup })));

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-xl text-center">
        <p className="font-display text-7xl font-bold text-gradient">404</p>
        <h1 className="mt-4 font-display text-2xl font-semibold">
          Ta strona nie istnieje
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Strona została przeniesiona lub usunięta. Sprawdź najpopularniejsze sekcje:
        </p>

        <div className="mt-8 grid gap-2 sm:grid-cols-2">
          <Link
            to="/platformy/baselinker"
            className="rounded-xl border border-border bg-card p-3 text-sm transition hover:border-primary hover:bg-accent"
          >
            Wdrożenia Baselinker
          </Link>
          <Link
            to="/platformy/shopify"
            className="rounded-xl border border-border bg-card p-3 text-sm transition hover:border-primary hover:bg-accent"
          >
            Wdrożenia Shopify
          </Link>
          <Link
            to="/integracje/sklepy-marketplace"
            className="rounded-xl border border-border bg-card p-3 text-sm transition hover:border-primary hover:bg-accent"
          >
            Sklepy & Marketplace
          </Link>
          <Link
            to="/integracje/erp-baselinker"
            className="rounded-xl border border-border bg-card p-3 text-sm transition hover:border-primary hover:bg-accent"
          >
            ERP & Baselinker
          </Link>
        </div>

        <Link
          to="/"
          className="mt-8 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary-glow px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition hover:scale-[1.03]"
        >
          Wróć na stronę główną
        </Link>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0d2a26" },
      { name: "author", content: "SyncTech" },
      { httpEquiv: "Content-Language", content: "pl" },
      // Bezpieczeństwo — ochrona przed XSS / clickjackingiem (PSI: "Skonfiguruj CSP").
      {
        httpEquiv: "Content-Security-Policy",
        content: [
          "default-src 'self'",
          "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://*.clarity.ms https://js.stripe.com https://assets.calendly.com",
          "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://assets.calendly.com",
          "font-src 'self' data: https://fonts.gstatic.com https://assets.calendly.com",
          "img-src 'self' data: blob: https:",
          "connect-src 'self' https://*.supabase.co https://*.google-analytics.com https://*.analytics.google.com https://*.googletagmanager.com https://*.clarity.ms https://api.stripe.com https://calendly.com https://*.calendly.com",
          "frame-src 'self' https://www.googletagmanager.com https://js.stripe.com https://hooks.stripe.com https://calendly.com https://*.calendly.com",
          "object-src 'none'",
          "base-uri 'self'",
          "form-action 'self'",
          "frame-ancestors 'self'",
          "upgrade-insecure-requests",
        ].join("; "),
      },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { title: "Lovable App" },
      { property: "og:title", content: "Lovable App" },
      { name: "twitter:title", content: "Lovable App" },
      { name: "description", content: "SyncTech is a comprehensive e-commerce marketing and integration platform." },
      { property: "og:description", content: "SyncTech is a comprehensive e-commerce marketing and integration platform." },
      { name: "twitter:description", content: "SyncTech is a comprehensive e-commerce marketing and integration platform." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/aac1fa80-959d-47c9-a9e3-1a31882b23a3/id-preview-f95ebc4c--90e67e65-5b35-42e7-8365-96ffec19411c.lovable.app-1779720853842.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/aac1fa80-959d-47c9-a9e3-1a31882b23a3/id-preview-f95ebc4c--90e67e65-5b35-42e7-8365-96ffec19411c.lovable.app-1779720853842.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { name: "google-site-verification", content: "W7eIk9l4awz36A0m8oD_bb7a6RKjEaaFspXI0Ybi26g" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48x48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Async load Google Fonts — nie blokuje first paint (~400-600ms FCP win na mobile)
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap",
        media: "print",
        onLoad: "this.media='all'",
      } as unknown as React.LinkHTMLAttributes<HTMLLinkElement>,
      { rel: "alternate", hrefLang: "pl", href: SITE_URL },
      { rel: "alternate", hrefLang: "x-default", href: SITE_URL },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pl" className="dark" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* noscript fallback dla fontów — jeśli JS wyłączony, ładujemy normalnie */}
        <noscript>
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap"
          />
        </noscript>
        <JsonLd data={organizationSchema()} />
        <JsonLd data={websiteSchema()} />
        <JsonLd data={professionalServiceSchema()} />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('theme')||'dark';document.documentElement.classList.toggle('dark',t!=='light');}catch(e){}})();`,
          }}
        />
      </head>
      <body>
        {/* Google Tag Manager (noscript) — fallback dla użytkowników bez JS */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-NGCDS2LH"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <ThemeProvider>
      <Outlet />
      <Suspense fallback={null}>
        <StickyCTA />
        <ExitIntentPopup />
        <Analytics />
      </Suspense>
    </ThemeProvider>
  );
}
